import { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';

import Button from '../../../../components/buttons/Button';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { isStrongPassword } from '../../../../components/PasswordStrengthMeter/passwordStrength';
import { PasswordStrengthMeter } from '../../../../components/PasswordStrengthMeter/PasswordStrengthMeter';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { RedesignEmailAndPasswordInputs } from '../../../../features/auth/redesign-components/RedesignEmailAndPasswordInputs/RedesignEmailAndPasswordInputs';
import { TermsAndServices } from '../../../../features/auth/redesign-components/SignUpOptionsRedesign/TermsAndServices';
import { ResponsibleSocialPlay } from '../../../../features/auth/ResponsibleSocialPlayTerms/ResponsibleSocialPlayTerms';
import SignUpConsents from '../../../../features/auth/SignUpConsents/SignUpConsents';
import {
  getEmailErrorMessageMap,
  getPasswordErrorMessageMap,
} from '../../../../features/auth/SignUpRegister/constants';
import { ThirdPartyAuth } from '../../../../features/auth/ThirdPartyAuth/ThirdPartyAuth';
import { useDirectRegCountrySelection, useNewDirectRegSignUp } from '../../../../features/auth/useNewLanderSignUp';
import { useSignUpWizardContext } from '../../../../features/auth/wizards/signUpWizard.provider';
import { useHandleSignUpWizard } from '../../../../features/auth/wizards/useHandleSIgnUpWizard';
import { useMarketingConsents } from '../../../../hooks/useMarketingConsents';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';
import { stripNonAsciiPassword } from '../../../../utils/validators';

import { useAuthForm } from './hooks/useAuthForm';
import classes from './SignupSection.module.scss';

interface SignupFormProps {
  onSignupSuccess?: () => void;
  wizardProps?: WizardChildProps;
  onSwitchToLogin: () => void;
}

export const SignupForm = observer(({ onSignupSuccess, wizardProps, onSwitchToLogin }: SignupFormProps) => {
  const { t } = useTranslation();
  const { marketingConsentStore, liveGeoStore } = useStores();
  const [showRSGDialog, setShowRSGDialog] = useState(false);
  const { email, password, isLoading, onEmailChange, onPasswordChange } = useAuthForm(onSignupSuccess);
  const { handleSignUpWizard } = useHandleSignUpWizard();
  // One terms checkbox (age is part of its copy, marketing consents move to the country select) vs three
  const isNewDirectReg = useNewDirectRegSignUp();
  // The consents move to the country select, so the checkboxes can only go when that step opens
  const isCountrySelectionEnabled = useDirectRegCountrySelection();
  const isSingleTerms = isNewDirectReg && isCountrySelectionEnabled;

  const { error, updateContextData } = useSignUpWizardContext();
  const [_signUpMarketingConsentsLS, setSignUpMarketingConsentsLS] = useLocalStorage<Record<string, boolean>>(
    'signup-marketing-consents',
    {},
  );

  const [marketingConsents, setMarketingConsents] = useState<Record<string, boolean>>({});
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [approveError, setApproveError] = useState('');
  const { countryCode } = liveGeoStore.rawGeo ?? {};

  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
  const { onChangeMarketingConsent, areAllOptionalConsentsChecked } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });
  const responsible_gaming_dialog = useFlag('responsible_gaming_dialog', ['']);
  const shouldShowRSGDialog = responsible_gaming_dialog.includes(countryCode || 'US') && !areAllOptionalConsentsChecked;

  const isApprovalFieldsValid = Boolean((isSingleTerms || isAgeConfirmed) && isTermsAccepted);
  const isPasswordStrong = isStrongPassword(password);

  useEffect(() => {
    if (!fetchingSignUpMarketingConsent) {
      if (signUpMarketingConsents.length > 0) {
        const map = signUpMarketingConsents.reduce(
          (acc, consent) => {
            acc[consent.id] = false;
            return acc;
          },
          {} as Record<string, boolean>,
        );

        setMarketingConsents(map);
      } else {
        setMarketingConsents({});
      }
    }
  }, [signUpMarketingConsents, fetchingSignUpMarketingConsent]);

  const validateApprovalFields = () => {
    if (!isApprovalFieldsValid) {
      setApproveError(t('auth.approvalConditionsRequired'));
      return false;
    }
    // Without checkboxes, overwrite any consents an earlier abandoned flow left in localStorage
    setSignUpMarketingConsentsLS(isSingleTerms ? {} : marketingConsents);
    setApproveError('');
    return true;
  };

  const handleProviderSignUp = () => {
    if (!validateApprovalFields()) {
      return false;
    }
    updateContextData({ provider: 'google' });

    if (shouldShowRSGDialog) {
      setShowRSGDialog(true);
      return false;
    }
    return true;
  };

  const handleEmailSignUp = () => {
    const credentials = { email: email.trim(), password };

    if (isLoading) {
      return;
    }

    if (!validateApprovalFields()) {
      return;
    }

    if (!email || !password) {
      updateContextData({ error: t('landing.direct.form.bothFieldsRequired') });
      return;
    }

    // The button stays disabled until the password is strong; this also stops an Enter-key submit.
    // OX Agent: Authentication vulnerability prevented by strong password policy
    if (!isStrongPassword(credentials.password)) {
      return;
    }

    if (shouldShowRSGDialog) {
      updateContextData({ provider: 'email', credentials });
      setShowRSGDialog(true);
    } else {
      updateContextData({ provider: 'email', credentials });
      handleSignUpWizard({
        provider: 'email',
        action: 'sign_up',
        credentials,
        wizardProps,
      });
    }
  };

  useEffect(() => {
    if (error) {
      setShowRSGDialog(false);
    }
  }, [error]);

  const onChangeMarketingConsentCheckBox = (checked: boolean, consentId: string) => {
    onChangeMarketingConsent(checked, consentId);
    setMarketingConsents((prev) => ({ ...prev, [consentId]: checked }));
  };

  const onTermsChange = (checked: boolean) => {
    setIsTermsAccepted(checked);
    if (checked) {
      setApproveError('');
      void analytics.sendEvent('age_approve', {
        event_category: 'sign_up',
        event_action: 'click',
        event_type: 'checkbox',
        event_value: 'terms_of_service',
      });
    }
  };

  const handleCloseResponsiblePlay = () => {
    setShowRSGDialog(false);
  };

  return (
    <div className={classes.signupSection}>
      {showRSGDialog && (
        <div className={classes.responsiblePlayPopup}>
          <div className={classes.responsiblePlayOverlay} onClick={handleCloseResponsiblePlay} />
          <div className={classes.responsiblePlayContent}>
            <ResponsibleSocialPlay
              {...(wizardProps as WizardChildProps)}
              wizardProps={wizardProps}
              onClose={handleCloseResponsiblePlay}
            />
          </div>
        </div>
      )}
      <div className={classes.googleSignupWrapper}>
        <ThirdPartyAuth
          action="Sign Up"
          className={classes.thirdPartyAuth}
          excludedProviders={['facebook', 'apple']}
          googleIconClassName={classes.googleIcon}
          hideLoader={true}
          onLoginAttempt={handleProviderSignUp}
        />
      </div>

      <div className={classes.orDivider}>
        <span className={classes.orText}>{t('common.or')}</span>
      </div>

      <div className={classes.emailFormWrapper}>
        <RedesignEmailAndPasswordInputs
          disabled={isLoading}
          emailErrorMessageMap={getEmailErrorMessageMap(t)}
          password={password}
          passwordErrorMessageMap={getPasswordErrorMessageMap(t)}
          type="sign_up"
          onChangeEmail={onEmailChange}
          onChangePassword={(newPassword) => onPasswordChange(stripNonAsciiPassword(newPassword))}
        />
        <PasswordStrengthMeter showHint className={classes.passwordStrengthMeter} password={password} />
      </div>

      <div className={classes.consentsWrapper}>
        {isSingleTerms ? (
          <TermsAndServices
            isError={!!approveError}
            isTermsAccepted={isTermsAccepted}
            setIsTermsAccepted={onTermsChange}
          />
        ) : (
          <SignUpConsents
            approveError={approveError}
            isAgeConfirmed={isAgeConfirmed}
            isTermsAccepted={isTermsAccepted}
            marketingConsents={marketingConsents}
            setIsAgeConfirmed={setIsAgeConfirmed}
            setIsTermsAccepted={setIsTermsAccepted}
            signUpMarketingConsents={signUpMarketingConsents}
            onChangeMarketingConsent={onChangeMarketingConsentCheckBox}
          />
        )}
      </div>

      <ErrorMessage noSpaceWhenEmpty className={classes.authErrorMessage}>
        {error || (isSingleTerms ? approveError : '')}
      </ErrorMessage>

      <Button
        btnType="main"
        className={classes.createAccountButton}
        dataTestId="signup-create-account-btn"
        // Strong password required by the UI only (Firebase's policy is the server-side check).
        disabled={isLoading || !isPasswordStrong}
        type="button"
        onClick={handleEmailSignUp}
      >
        {t('auth.createAccount')}
      </Button>

      <div className={classes.loginLink} data-testid="direct-signup-already-have-account">
        <Trans
          components={{
            question: <span />,
            login: <span className={classes.loginLinkText} onClick={onSwitchToLogin} />,
          }}
          i18nKey="landing.direct.alreadyHaveAccountLogIn"
        />
      </div>
    </div>
  );
});
